import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { InvestigationEntity } from '../../domain/entities/investigation.entity';
import { InvestigationStatusSnapshotEntity } from '../../domain/entities/investigation-status-snapshot.entity';
import { InvestigationPrioritySnapshotEntity } from '../../domain/entities/investigation-priority-snapshot.entity';
import { InvestigationFieldValueEntity } from '../../domain/entities/investigation-field-value.entity';
import { InvestigationAttachmentEntity } from '../../domain/entities/investigation-attachment.entity';
import { InvestigationInvolvedEmployeeEntity } from '../../domain/entities/investigation-involved-employee.entity';
import { InvestigationWatcherEntity } from '../../domain/entities/investigation-watcher.entity';
import { WatcherOptionEntity } from '../../domain/entities/watcher-option.entity';
import { InvestigationMessageEntity } from '../../domain/entities/investigation-message.entity';
import { InvestigationWorkNoteEntity } from '../../domain/entities/investigation-work-note.entity';
import { InvestigationClosingReasonEntity } from '../../domain/entities/investigation-closing-reason.entity';
import { InvestigationReopeningReasonEntity } from '../../domain/entities/investigation-reopening-reason.entity';
import { InvestigationConversionReasonEntity } from '../../domain/entities/investigation-conversion-reason.entity';
import { InvestigationLinkedInvestigationEntity } from '../../domain/entities/investigation-linked-investigation.entity';
import { InvestigationChecklistStepEntity } from '../../domain/entities/investigation-checklist-step.entity';
import { InvestigationEmailTemplateSnapshotEntity } from '../../domain/entities/investigation-email-template-snapshot.entity';
import {
  AssignableAgentEntity,
  InvestigationAssignmentOptionsEntity,
} from '../../domain/entities/investigation-assignment-options.entity';
import { InvestigationAssigneeCandidateEntity } from '../../domain/entities/investigation-assignee-candidate.entity';
import type { InvestigationAssigneeCandidateResponseDto } from '../dtos/investigation-assignee-candidates-response.dto';
import { InvestigationsApi } from '../api/investigations.api';
import { InvestigationsMapper } from '../mappers/investigations.mapper';
import { ParentChildInvestigationItemEntity, ParentChildInvestigationsEntity } from '../../domain/entities/investigation-parent-child.entity';
import { InvestigationParentChildItemDto } from '../dtos/investigation-parent-child-response.dto';
import { CreateInvestigationResponseDto } from '../dtos/create-investigation-response.dto';
import { CreateEmployeeInvestigationRequestDto } from '../dtos/create-employee-investigation-request.dto';
import { CreateEmployeeInvestigationCommand } from '../../domain/commands';
import { TransferredCaseFieldValueEntity } from '../../domain/entities/transferred-case-field-value.entity';
import { ReclassificationOptionEntity } from '../../domain/entities/reclassification-option.entity';
import { S3UploadApi } from '@shared';

@Injectable({ providedIn: 'root' })
export class InvestigationsRepository {
  private readonly _api = inject(InvestigationsApi);
  private readonly _mapper = inject(InvestigationsMapper);
  private readonly _s3Api = inject(S3UploadApi);

  public createInvestigation(data: Partial<InvestigationEntity>): Observable<CreateInvestigationResponseDto> {
    return this._api.createInvestigation(this._mapper.toCreateDto(data));
  }

  /**
   * Creates a new Investigation via employee portal endpoint.
   * @param command - Create employee investigation command
   * @returns Observable with the created Investigation response (investigationId and investigationNumber)
   */
  public createEmployeeInvestigation(command: CreateEmployeeInvestigationCommand): Observable<CreateInvestigationResponseDto> {
    const requestDto: CreateEmployeeInvestigationRequestDto = this._mapper.toCreateEmployeeDto(command);
    return this._api.createEmployeeInvestigation(requestDto);
  }

  public getInvestigationById(id: string): Observable<InvestigationEntity> {
    return this._api.getInvestigationById(id).pipe(
      map((dto) => this._mapper.toDomain(dto)),
    );
  }

  public getStatusSnapshots(id: string): Observable<InvestigationStatusSnapshotEntity[]> {
    return this._api.getStatusSnapshots(id).pipe(
      map((dtos) => this._mapper.toStatusSnapshotsDomain(dtos)),
    );
  }

  public getPrioritySnapshots(id: string): Observable<InvestigationPrioritySnapshotEntity[]> {
    return this._api.getPrioritySnapshots(id).pipe(
      map((dtos) => this._mapper.toPrioritySnapshotsDomain(dtos)),
    );
  }

  public getFieldValues(id: string): Observable<InvestigationFieldValueEntity[]> {
    return this._api.getFieldValues(id).pipe(
      map((dtos) => this._mapper.toFieldValuesDomain(dtos)),
    );
  }

  public updateStatus(id: string, statusId: string): Observable<void> {
    return this._api.updateStatus(id, { statusId });
  }

  public updatePriority(id: string, priorityCode: string): Observable<void> {
    return this._api.updatePriority(id, { priorityCode });
  }

  public updateAssignees(id: string, primaryAssigneeUserId: string | null, secondaryAssigneeUserId: string | null): Observable<void> {
    return this._api.updateAssignees(id, { primaryAssigneeUserId, secondaryAssigneeUserId });
  }

  public getInvestigationTypeAssignmentOptions(investigationTypeId: string, search?: string): Observable<InvestigationAssignmentOptionsEntity> {
    return this._api.getInvestigationTypeAssignmentOptions(investigationTypeId, search).pipe(
      map((dto) => {
        const entity = new InvestigationAssignmentOptionsEntity();
        entity.investigationTypeId = dto.investigationTypeId;
        entity.ruleKind = dto.ruleKind;
        entity.teamId = dto.teamId;
        entity.suggestedPrimaryUserId = dto.suggestedPrimaryUserId;
        entity.allowsPrimaryEdit = dto.allowsPrimaryEdit;
        entity.allowsSecondary = dto.allowsSecondary;
        entity.candidateAgents = (dto.candidateAgents ?? []).map((a) => {
          const agent = new AssignableAgentEntity();
          agent.userId = a.userId;
          agent.teamId = a.teamId;
          agent.firstName = a.firstName;
          agent.lastName = a.lastName;
          agent.email = a.email;
          agent.isManager = a.isManager;
          return agent;
        });
        return entity;
      })
    );
  }

  public getInvestigationAssigneeCandidates(investigationId: string): Observable<InvestigationAssigneeCandidateEntity[]> {
    return this._api.getInvestigationAssigneeCandidates(investigationId).pipe(
      map((dtos) => dtos.map((dto) => this._mapCandidateDto(dto)))
    );
  }

  public getBulkInvestigationAssignmentOptions(investigationIds: string[]): Observable<InvestigationAssigneeCandidateEntity[]> {
    return this._api.getBulkInvestigationAssignmentOptions(investigationIds).pipe(
      map((dtos) => dtos.map((dto) => this._mapCandidateDto(dto)))
    );
  }

  private _mapCandidateDto(dto: InvestigationAssigneeCandidateResponseDto): InvestigationAssigneeCandidateEntity {
    const candidate = new InvestigationAssigneeCandidateEntity();
    candidate.userId = dto.userId;
    candidate.firstName = dto.firstName;
    candidate.lastName = dto.lastName;
    candidate.email = dto.email;
    candidate.isManager = dto.isManager;
    candidate.teamIds = dto.teamIds ?? [];
    return candidate;
  }

  public updateFieldValues(id: string, fieldValues: InvestigationFieldValueEntity[]): Observable<void> {
    return this._api.updateFieldValues(id, {
      fieldValues: fieldValues.map((fv) => ({ versionFieldId: fv.versionFieldId, value: fv.value })),
    });
  }

  // Attachments
  public getAttachments(id: string): Observable<InvestigationAttachmentEntity[]> {
    return this._api.getAttachments(id).pipe(
      map((dtos) => this._mapper.toAttachmentsDomain(dtos)),
    );
  }

  public uploadAttachment(id: string, file: File, onProgress?: (progress: number) => void): Observable<InvestigationAttachmentEntity> {
    return this._api.getAttachmentUploadUrl(id, {
      fileName: file.name,
      contentType: file.type,
      fileSizeBytes: file.size,
    }).pipe(
      switchMap((uploadResponse) =>
        this._s3Api.uploadToS3(uploadResponse.uploadUrl, file, uploadResponse.requiredHeaders, onProgress).pipe(
          switchMap(() => this._api.confirmAttachmentUpload(id, { uploadId: uploadResponse.uploadId })),
          map((confirmResponse) => {
            const entity = new InvestigationAttachmentEntity();
            entity.id = confirmResponse.id;
            entity.fileName = file.name;
            entity.contentType = file.type;
            entity.fileSizeBytes = file.size;
            entity.filePath = '';
            return entity;
          }),
        )
      ),
    );
  }

  public getAttachmentDownloadUrl(id: string, attachmentId: string): Observable<string> {
    return this._api.getAttachmentDownloadUrl(id, attachmentId).pipe(
      map((dto) => dto.downloadUrl),
    );
  }

  public deleteAttachment(id: string, attachmentId: string): Observable<void> {
    return this._api.deleteAttachment(id, attachmentId);
  }

  // Involved employees
  public getInvolvedEmployees(id: string): Observable<InvestigationInvolvedEmployeeEntity[]> {
    return this._api.getInvolvedEmployees(id).pipe(
      map((dtos) => this._mapper.toInvolvedEmployeesDomain(dtos)),
    );
  }

  public addInvolvedEmployees(id: string, userIds: string[]): Observable<void> {
    return this._api.addInvolvedEmployees(id, { userIds });
  }

  public removeInvolvedEmployee(id: string, userId: string): Observable<void> {
    return this._api.removeInvolvedEmployee(id, userId);
  }

  // Watchers
  public getWatchers(id: string): Observable<InvestigationWatcherEntity[]> {
    return this._api.getWatchers(id).pipe(
      map((dtos) => this._mapper.toWatchersDomain(dtos)),
    );
  }

  public getWatcherOptions(id: string): Observable<WatcherOptionEntity[]> {
    return this._api.getWatcherOptions(id).pipe(
      map((dtos) => this._mapper.toWatcherOptionsDomain(dtos)),
    );
  }

  public watchInvestigation(id: string): Observable<void> {
    return this._api.watchInvestigation(id);
  }

  public unwatchInvestigation(id: string): Observable<void> {
    return this._api.unwatchInvestigation(id);
  }

  public addWatcher(id: string, userId: string): Observable<void> {
    return this._api.addWatcher(id, userId);
  }

  public removeWatcher(id: string, userId: string): Observable<void> {
    return this._api.removeWatcher(id, userId);
  }

  // Messages
  public getMessages(id: string): Observable<InvestigationMessageEntity[]> {
    return this._api.getMessages(id).pipe(
      map((dtos) => this._mapper.toMessagesDomain(dtos)),
    );
  }

  public addMessage(id: string, messageType: number, body: string): Observable<{ id: string }> {
    return this._api.addMessage(id, messageType, body);
  }

  public markMessagesAsRead(id: string): Observable<void> {
    return this._api.markMessagesAsRead(id);
  }

  // Work Notes
  public getWorkNotes(id: string): Observable<InvestigationWorkNoteEntity[]> {
    return this._api.getWorkNotes(id).pipe(
      map((dtos) => this._mapper.toWorkNotesDomain(dtos)),
    );
  }

  public addWorkNote(id: string, body: string): Observable<{ id: string }> {
    return this._api.addWorkNote(id, body);
  }

  // Parent/child investigations
  public getParentChildInvestigations(id: string): Observable<ParentChildInvestigationsEntity> {
    return this._api.getParentChildInvestigations(id).pipe(
      map((dto) => {
        const entity = new ParentChildInvestigationsEntity();
        entity.parentInvestigation = dto.parent
          ? this._mapParentChildItem(dto.parent)
          : null;
        entity.childInvestigations = (dto.children ?? []).map((c) =>
          this._mapParentChildItem(c)
        );
        return entity;
      }),
    );
  }

  private _mapParentChildItem(dto: InvestigationParentChildItemDto): ParentChildInvestigationItemEntity {
    const item = new ParentChildInvestigationItemEntity();
    item.id = dto.id;
    item.investigationId = dto.linkedInvestigationId;
    item.investigationNumber = dto.linkedInvestigationNumber;
    item.investigationTypeName = dto.linkedInvestigationTypeName;
    item.statusName = dto.linkedInvestigationStatusName;
    item.statusColor = dto.linkedInvestigationStatusColor;
    item.requestorName = dto.linkedInvestigationRequestorName;
    item.linkType = (dto.linkType === 'child' ? 'parent' : dto.linkType === 'parent' ? 'child' : dto.linkType) as 'parent' | 'child';
    return item;
  }

  public addChildInvestigations(id: string, childInvestigationIds: string[]): Observable<void> {
    return this._api.addChildInvestigations(id, childInvestigationIds);
  }

  public setParentInvestigation(id: string, parentInvestigationId: string): Observable<void> {
    return this._api.setParentInvestigation(id, parentInvestigationId);
  }

  public unlinkParentChildInvestigation(id: string, linkedInvestigationId: string): Observable<void> {
    return this._api.unlinkParentChildInvestigation(id, linkedInvestigationId);
  }

  public updateInvestigationLinkType(id: string, linkedInvestigationId: string, linkType: 'parent' | 'child' | 'related'): Observable<void> {
    return this._api.updateInvestigationLinkType(id, linkedInvestigationId, linkType);
  }

  // Linked investigations
  public getLinkedInvestigations(id: string): Observable<InvestigationLinkedInvestigationEntity[]> {
    return this._api.getLinkedInvestigations(id).pipe(
      map((dtos) => this._mapper.toLinkedInvestigationsDomain(dtos)),
    );
  }

  public linkInvestigation(id: string, linkedInvestigationId: string, linkType = 'related'): Observable<void> {
    return this._api.linkInvestigation(id, linkedInvestigationId, linkType);
  }

  public unlinkInvestigation(id: string, linkedInvestigationId: string): Observable<void> {
    return this._api.unlinkInvestigation(id, linkedInvestigationId);
  }

  // Checklist steps
  public getChecklistSteps(id: string): Observable<InvestigationChecklistStepEntity[]> {
    return this._api.getChecklistSteps(id).pipe(
      map((dtos) => this._mapper.toChecklistStepsDomain(dtos)),
    );
  }

  public updateChecklistStep(id: string, stepId: string, isCompleted: boolean): Observable<void> {
    return this._api.updateChecklistStep(id, stepId, isCompleted);
  }

  public getEmailTemplateSnapshots(id: string): Observable<InvestigationEmailTemplateSnapshotEntity[]> {
    return this._api.getEmailTemplateSnapshots(id).pipe(
      map((dtos) => this._mapper.toEmailTemplateSnapshotsDomain(dtos)),
    );
  }

  // Print
  public exportLegalPacket(id: string, sections: string[]): Observable<Blob> {
    return this._api.exportLegalPacket(id, sections);
  }

  public getInvestigationForPrint(id: string): Observable<{
    investigation: InvestigationEntity;
    fieldValues: { label: string; value: string; sortOrder: number }[];
    involvedEmployees: { firstName: string; lastName: string; email: string }[];
  }> {
    return this._api.getInvestigationForPrint(id).pipe(
      map((dto) => ({
        investigation: this._mapper.toDomain(dto.investigationDetail),
        fieldValues: dto.fieldValues
          .filter((fv) => fv.value != null && fv.value !== '')
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((fv) => ({ label: fv.fieldLabel, value: fv.value ?? '', sortOrder: fv.sortOrder })),
        involvedEmployees: dto.involvedEmployees.map((e) => ({
          firstName: e.firstName,
          lastName: e.lastName,
          email: e.email,
        })),
      })),
    );
  }

  // Close / Reopen
  public getClosingReasons(): Observable<InvestigationClosingReasonEntity[]> {
    return this._api.getClosingReasons().pipe(
      map((dtos) => this._mapper.toClosingReasonsDomain(dtos)),
    );
  }

  public getReopeningReasons(): Observable<InvestigationReopeningReasonEntity[]> {
    return this._api.getReopeningReasons().pipe(
      map((dtos) => this._mapper.toReopeningReasonsDomain(dtos)),
    );
  }

  public getConversionReasons(): Observable<InvestigationConversionReasonEntity[]> {
    return this._api.getConversionReasons().pipe(
      map((dtos) => this._mapper.toConversionReasonsDomain(dtos)),
    );
  }

  public closeInvestigation(id: string, statusId: string, closingReasonId: string, closingNotes: string): Observable<void> {
    return this._api.closeInvestigation(id, statusId, closingReasonId, closingNotes);
  }

  public convertToInvestigation(id: string, conversionReasonId: string, conversionNotes: string): Observable<void> {
    return this._api.convertToInvestigation(id, conversionReasonId, conversionNotes);
  }

  public reopenInvestigation(id: string, reopeningReasonId: string, reopeningNotes: string): Observable<void> {
    return this._api.reopenInvestigation(id, reopeningReasonId, reopeningNotes);
  }

  // Transfer-to-ER (HCMI-796)
  public getTransferredCaseFields(id: string): Observable<TransferredCaseFieldValueEntity[]> {
    return this._api.getTransferredCaseFields(id).pipe(
      map((dtos) => this._mapper.toTransferredCaseFieldValuesDomain(dtos)),
    );
  }

  public changeInvestigationType(id: string, investigationTypeId: string): Observable<void> {
    return this._api.changeInvestigationType(id, { investigationTypeId });
  }

  public getReclassificationOptions(): Observable<ReclassificationOptionEntity[]> {
    return this._api.getReclassificationOptions().pipe(
      map((dtos) => dtos.map((dto) => Object.assign(new ReclassificationOptionEntity(), dto))),
    );
  }
}
